import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { OpenLocationCode } from "open-location-code";
import { QRCodeSVG } from "qrcode.react";
import { MapPin, LocateFixed, AlertTriangle, Check, QrCode } from "lucide-react";
import { toast } from "sonner";

// Fix default marker icons (Leaflet + Webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const olc = new OpenLocationCode();

function ClickMarker({ position, setPosition }) {
    useMapEvents({
        click(e) { setPosition([e.latlng.lat, e.latlng.lng]); },
    });
    const markerRef = useRef(null);
    return position ? (
        <Marker
            draggable
            eventHandlers={{
                dragend() {
                    const m = markerRef.current;
                    if (m) { const p = m.getLatLng(); setPosition([p.lat, p.lng]); }
                },
            }}
            position={position}
            ref={markerRef}
        />
    ) : null;
}

function Recenter({ position, zoom }) {
    const map = useMap();
    useEffect(() => { if (position) map.flyTo(position, zoom || 17, { duration: 0.6 }); }, [position, zoom, map]);
    return null;
}

/**
 * LocationPicker
 * Props:
 *  - value: { plus_code, lat, lng, confirmed } | null
 *  - onChange: (value) => void
 *  - departamentoName / municipioName: strings used to complete the human-readable Plus Code.
 *  - centerHint: { lat, lng } — auto-fly map to this centroid when it changes (before the user marks a point).
 */
export default function LocationPicker({ value, onChange, departamentoName, municipioName, centerHint }) {
    const [position, setPosition] = useState(value?.lat && value?.lng ? [value.lat, value.lng] : null);
    const [confirmed, setConfirmed] = useState(!!value?.confirmed);
    const [busy, setBusy] = useState(false);
    const [hintPosition, setHintPosition] = useState(null);

    // Default center: San Salvador
    const defaultCenter = useMemo(() => [13.6929, -89.2182], []);

    // Nominatim (OpenStreetMap) geocode cache — avoids re-hitting for same municipio.
    const geocodeCacheRef = useRef({});

    // When the parent tells us the customer selected a departamento/municipio, fly the map there
    // — but only if the user hasn't marked their exact point yet.
    // Priority: geocoded MUNICIPIO (precise) > centerHint (departamento centroid, fallback).
    useEffect(() => {
        if (position) return; // User already placed their point — don't move.

        // Set immediate hint from departamento centroid so the map isn't stuck at national view
        if (centerHint) setHintPosition([centerHint.lat, centerHint.lng]);

        // If a municipio was chosen, refine the hint via Nominatim.
        if (!municipioName) return;
        const key = `${municipioName}|${departamentoName || ""}`;
        const cached = geocodeCacheRef.current[key];
        if (cached) {
            setHintPosition([cached.lat, cached.lng]);
            return;
        }
        const controller = new AbortController();
        const q = encodeURIComponent(
            `${municipioName}, ${departamentoName ? departamentoName + ", " : ""}El Salvador`
        );
        fetch(
            `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=sv&q=${q}`,
            { signal: controller.signal, headers: { Accept: "application/json" } }
        )
            .then((r) => r.ok ? r.json() : [])
            .then((arr) => {
                if (!Array.isArray(arr) || arr.length === 0) return;
                const hit = arr[0];
                const lat = parseFloat(hit.lat);
                const lng = parseFloat(hit.lon);
                if (Number.isFinite(lat) && Number.isFinite(lng)) {
                    geocodeCacheRef.current[key] = { lat, lng };
                    setHintPosition([lat, lng]);
                }
            })
            .catch(() => { /* silent — fallback to department centroid */ });
        return () => controller.abort();
    }, [centerHint, position, municipioName, departamentoName]);

    const plusCodeFull = useMemo(() => {
        if (!position) return "";
        try { return olc.encode(position[0], position[1], 10); }
        catch { return ""; }
    }, [position]);

    // Short local part: e.g. "5VHP+FWW"
    const shortCode = useMemo(() => {
        if (!plusCodeFull) return "";
        const parts = plusCodeFull.split("+");
        if (parts.length !== 2) return plusCodeFull;
        return (parts[0].slice(-4) + "+" + parts[1]).toUpperCase();
    }, [plusCodeFull]);

    // Human-readable full label: "5VHP+FWW, MUNICIPIO, El Salvador"
    // Google Maps requires the locality context for short Plus Codes to resolve.
    const displayLocality = useMemo(() => {
        const parts = [];
        if (municipioName) parts.push(municipioName);
        else if (departamentoName) parts.push(departamentoName);
        parts.push("El Salvador");
        return parts.join(", ");
    }, [municipioName, departamentoName]);

    const humanReadable = shortCode ? `${shortCode}, ${displayLocality}` : "";

    // Google Maps URL that the courier can open by scanning the QR
    const mapsUrl = useMemo(() => {
        if (!position) return "";
        // Prefer coords for absolute precision; include Plus Code + locality as fallback label
        return `https://www.google.com/maps/place/${position[0].toFixed(6)},${position[1].toFixed(6)}?q=${encodeURIComponent(humanReadable)}`;
    }, [position, humanReadable]);

    const useMyLocation = () => {
        if (!navigator.geolocation) { toast.error("Tu navegador no permite geolocalización"); return; }
        setBusy(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setPosition([pos.coords.latitude, pos.coords.longitude]);
                setConfirmed(false);
                setBusy(false);
                toast.success("Ubicación detectada. Ajústala en el mapa si es necesario.");
            },
            (err) => {
                setBusy(false);
                toast.error(err.code === 1
                    ? "Permiso de ubicación denegado. Habilítalo en tu navegador."
                    : "No se pudo obtener tu ubicación. Toca en el mapa para marcarla.");
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
        );
    };

    const confirm = () => {
        if (!position) { toast.error("Primero marca tu ubicación en el mapa"); return; }
        setConfirmed(true);
        onChange({
            plus_code: shortCode,
            plus_code_full: plusCodeFull,
            plus_code_human: humanReadable,
            lat: position[0],
            lng: position[1],
            maps_url: mapsUrl,
            confirmed: true,
        });
        toast.success(`Ubicación confirmada · Plus Code ${shortCode}`);
    };

    // Push updates upstream when the marker moves
    useEffect(() => {
        if (!position) return;
        onChange({
            plus_code: shortCode,
            plus_code_full: plusCodeFull,
            plus_code_human: humanReadable,
            lat: position[0],
            lng: position[1],
            maps_url: mapsUrl,
            confirmed,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [position, confirmed]);

    // The active point that Recenter should fly to
    const flyTo = position || hintPosition;
    // Zoom: exact point → 17 · municipio (geocoded / centroid) → 14 · departamento only → 11
    const flyZoom = position ? 17 : (municipioName ? 14 : 11);

    return (
        <div className="border border-[var(--tb-gold)]/40 bg-[var(--tb-border-gold)]/5 rounded p-4 space-y-3" data-testid="location-picker">
            <div className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-[var(--tb-gold)] flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                    <div className="font-medium text-sm">Ubicación exacta de entrega (Plus Code)</div>
                    <p className="text-xs text-[var(--tb-text-2)] mt-0.5">
                        Complementa tu dirección con un punto GPS. El mensajero podrá llegar más rápido y sin errores. No reemplaza la dirección escrita.
                    </p>
                </div>
            </div>

            {/* Warning — black text for readability */}
            <div className="bg-amber-400 border border-amber-500 p-2.5 rounded flex items-start gap-2 text-xs text-black">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                    <strong>Importante:</strong> asegúrate de estar <strong>físicamente en tu dirección de entrega</strong> cuando uses &quot;Usar mi ubicación actual&quot;. Si estás en otro lugar, marca el punto correcto tocando en el mapa.
                </span>
            </div>

            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={useMyLocation}
                    disabled={busy}
                    className="tb-btn-secondary !text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
                    data-testid="loc-use-current"
                >
                    <LocateFixed className="w-3.5 h-3.5" />
                    {busy ? "Detectando..." : "Usar mi ubicación actual"}
                </button>
                <div className="text-xs text-[var(--tb-muted)] self-center">o toca el mapa abajo para marcar tu punto</div>
            </div>

            <div className="h-64 rounded overflow-hidden border border-[var(--tb-border)]" data-testid="loc-map-container">
                <MapContainer center={flyTo || defaultCenter} zoom={flyTo ? flyZoom : 9} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <ClickMarker position={position} setPosition={(p) => { setPosition(p); setConfirmed(false); }} />
                    <Recenter position={flyTo} zoom={flyZoom} />
                </MapContainer>
            </div>

            {position && (
                <div className="flex flex-wrap items-start gap-3 pt-1">
                    <div className="flex-1 min-w-[200px]">
                        <div className="tb-overline text-[10px] text-[var(--tb-muted)]">Plus Code (ciudad y país incluidos)</div>
                        <div className="font-mono text-lg text-[var(--tb-gold)] leading-tight" data-testid="loc-plus-code">{humanReadable}</div>
                        <div className="text-[10px] text-[var(--tb-muted)] mt-1">Lat: {position[0].toFixed(6)} · Lng: {position[1].toFixed(6)}</div>
                    </div>
                    {/* Live QR preview */}
                    {mapsUrl && (
                        <div className="bg-white p-2 rounded border border-[var(--tb-border)]" title="Escanea para abrir en Google Maps" data-testid="loc-qr">
                            <QRCodeSVG value={mapsUrl} size={72} level="M" />
                            <div className="text-[9px] text-black text-center mt-1 flex items-center justify-center gap-1"><QrCode className="w-2.5 h-2.5" /> Google Maps</div>
                        </div>
                    )}
                    {confirmed ? (
                        <span className="text-xs px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 inline-flex items-center gap-1 self-center" data-testid="loc-confirmed-badge">
                            <Check className="w-3.5 h-3.5" /> Ubicación confirmada
                        </span>
                    ) : (
                        <button
                            type="button"
                            onClick={confirm}
                            className="tb-btn-primary !text-xs inline-flex items-center gap-1.5 self-center"
                            data-testid="loc-confirm"
                        >
                            <Check className="w-3.5 h-3.5" /> Confirmar esta ubicación
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
